// Searchable Lucide icon picker via a Popover + grid.

import { useState } from "react";
import * as LucideIcons from "lucide-react";
import { ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ICON_LIST } from "./lucideIconList";

// Convert kebab-case to PascalCase for lucide-react lookup.
function toPascalCase(kebab: string): string {
  return kebab
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

function LucideIcon({ name, className }: { name: string; className?: string }) {
  const pascalName = toPascalCase(name);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Icon = (LucideIcons as Record<string, any>)[pascalName];
  if (!Icon) return null;
  return <Icon className={className} />;
}

interface IconPickerProps {
  value: string | null;
  onChange: (icon: string) => void;
  disabled?: boolean;
}

export function IconPicker({ value, onChange, disabled }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search.trim()
    ? ICON_LIST.filter((name) => name.includes(search.toLowerCase().trim()))
    : ICON_LIST;

  function handleSelect(icon: string) {
    onChange(icon);
    setOpen(false);
    setSearch("");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="gap-2 w-32 justify-between"
          aria-label="Pick icon"
        >
          <span className="flex items-center gap-1.5 min-w-0">
            {value ? (
              <LucideIcon name={value} className="w-3.5 h-3.5 flex-shrink-0" />
            ) : (
              <span className="text-muted-foreground text-xs">No icon</span>
            )}
            {value && (
              <span className="text-xs truncate text-muted-foreground hidden sm:inline">
                {value}
              </span>
            )}
          </span>
          <ChevronDown className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-3 space-y-2"
        align="start"
        data-testid="icon-picker-popover"
      >
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search icons..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
            autoFocus
          />
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">No icons match</p>
        ) : (
          <div className="grid grid-cols-8 gap-1 max-h-52 overflow-y-auto">
            {filtered.map((name) => (
              <button
                key={name}
                type="button"
                title={name}
                aria-label={name}
                onClick={() => handleSelect(name)}
                className={cn(
                  "w-10 h-10 rounded-md flex items-center justify-center transition-colors",
                  "hover:bg-white/[0.06]",
                  value === name && "bg-indigo-500/15"
                )}
              >
                <LucideIcon name={name} className="w-4 h-4" />
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
